<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="flex items-center justify-center min-h-screen text-center">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel (Fullsize) -->
      <div
        class="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-4 w-[98vw] h-[95vh]">
        <div class="bg-white px-6 pt-6 pb-4 sm:p-8 sm:pb-6 h-[calc(95vh-70px)] overflow-y-auto overflow-x-hidden">
          <div class="flex justify-between items-start">
            <h3 class="text-xl font-bold text-gray-900 flex items-center justify-between w-full">
              <div class="flex gap-5">
                <div>
                  <span>{{ documentData.name }}</span>
                </div>
                <div class="flex gap-2">
                  <div class="flex-shrink-0">
                    <img v-if="documentData.created_by_avatar_url" :src="documentData.created_by_avatar_url"
                      alt="Avatar" class="h-8 w-8 rounded-full mr-2" />
                    <div v-else class="h-8 w-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                      <span class="text-gray-500 text-sm">{{ documentData.created_by_name?.charAt(0) || '?' }}</span>
                    </div>
                  </div>
                  <span>{{ documentData.created_by_name }}</span>
                </div>
                <div class="flex items-center mt-1">
                  <div class="w-3 h-3 rounded-full mr-2" :class="{
                    'bg-green-700': documentData.status === DOCUMENT_STATUS.UPLOADED_CHECKING
                  }"></div>
                  <span class="text-sm">{{ formatDocumentStatus(documentData.status) }}</span>
                </div>
              </div>
              <button @click="commentsVisible = !commentsVisible"
                class="ml-4 px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm">Toggle Comments</button>
            </h3>

            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Two Spreadsheet Demo Component -->
          <div class="mt-4 grid grid-cols-4">
            <div :class="{ 'col-span-3': commentsVisible, 'col-span-4': !commentsVisible }">
              <DocumentComparisonSpreadsheet :document-data="documentData" @close="close" />
            </div>
            <div v-if="commentsVisible" class="col-span-1 px-4">
              <CommentList :referenceId="documentData.id" />
            </div>
          </div>

          <!-- Review Instructions -->
          <div class="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 class="text-sm font-medium text-blue-700">Review Instructions</h4>
            <p class="mt-1 text-sm text-blue-600">
              Please review the uploaded template and findings above, then decide whether to approve or decline it.
              If approved, the document will be moved to "Ready for Revision".
              If declined, the document will be moved to "Adjustment Needed" and the owner will be notified.
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="mt-6 flex items-center justify-center py-8">
            <div class="text-gray-500 flex flex-col items-center">
              <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
              </svg>
              <p>Processing...</p>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="successMessage"
            class="mt-4 p-3 bg-green-100 border border-green-300 rounded-md text-green-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>{{ successMessage }}</span>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage"
            class="mt-4 p-3 bg-red-100 border border-red-300 rounded-md text-red-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{{ errorMessage }}</span>
          </div>
        </div>

        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
          <button @click="close"
            class="mr-2 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            :disabled="isLoading">
            Cancel
          </button>
          <button @click="declineTemplate"
            class="mr-2 inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            :disabled="isLoading">
            Decline
          </button>
          <button @click="approveTemplate"
            class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
            :disabled="isLoading">
            Approve
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { DOCUMENT_STATUS } from '@/types/document';
import { logger, LogCategory } from '@/services/logger';
import { useTasks } from '@/composables/useTasks';
import { supabase } from '@/services/supabase';
import { useDueDates } from '@/composables/useDueDates';
import { ACTIONS } from '@/types/taskActions';
import DocumentComparisonSpreadsheet from '@/components/DocumentComparisonSpreadsheet.vue';
import CommentList from '@/components/comments/CommentList.vue';
export default defineComponent({
  name: 'UploadedTemplateReviewModal',
  components: {
    DocumentComparisonSpreadsheet,
    CommentList
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    documentData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'status-updated'],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const successMessage = ref<string | null>(null);
    const errorMessage = ref<string | null>(null);
    const commentsVisible = ref(true);

    
    // Import tasks composable
    const { tasks, openReviewTasks, fetchTasks, completeTask, createTask } = useTasks();
    
    // Constants
    const PRESENTATION_DOCUMENT_COUNT = 15; // Number of documents needed to trigger presentation task creation
    
    /**
     * Completes all review tasks for a specific document
     * (Review tasks have been created for all users with the business role "CONTROLLER", the first one 
     * who is reviewind the document should complete all tasks to avoid dangling tasks)
     * @param documentId The ID of the document
     * @param documentName The name of the document
     * @param result The result of the review (approved or rejected)
     */
    const completeReviewTasksForDocument = async (documentId: string, documentName: string, result: string) => {
      await fetchTasks();
      
      // Get current user ID from session
      const { data: userData } = await supabase.auth.getSession();
      const currentUserId = userData?.session?.user.id;
      
      // Get user name from profiles table using currentUserId
      let userName = 'Unknown User';
      if (currentUserId) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', currentUserId)
          .single();
          
        if (profileData && profileData.name) {
          userName = profileData.name;
        }
      }
      
      // Filter review tasks (could be more than one, see comment above) by document ID
      const tasksToComplete = openReviewTasks.value.filter(task =>
        task.document_id === documentId
      );
      
      logger.info('Completing review tasks after template approval', LogCategory.GENERAL, {
        documentId,
        tasksCount: tasksToComplete.length,
        approvedBy: userName
      });
      
      // Complete all matching tasks
      for (const task of tasksToComplete) {
        await completeTask(
          task.id,
          `Document ${documentName} ${result} by ${userName}`
        );
      }
    };
    
    /**
     * Creates an ADJUST task for the document assigned to the original uploader
     * @param documentId The ID of the document
     * @param documentName The name of the document
     */
    const createAdjustTaskForDocument = async (documentId: string, documentName: string) => {
      // Fetch all tasks to ensure we have the latest data
      await fetchTasks();
      
      // Find the latest completed UPLOAD task for this document to find the assigned user
      const uploadTasks = tasks.value.filter(task =>
        task.document_id === documentId &&
        task.action === 'UPLOAD' &&
        task.done_at !== null
      );
      
      // Sort by done_at timestamp (latest first)
      uploadTasks.sort((a, b) => {
        const dateA = new Date(a.done_at || 0);
        const dateB = new Date(b.done_at || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      // Get the user ID from the latest task
      if (uploadTasks.length > 0) {
        const latestTask = uploadTasks[0];
        const assignedUserId = latestTask.assigned_user_id;
        
        // Calculate due date using the getDueDate function
        const { getDueDate } = useDueDates();
        const dueDateString = await getDueDate(ACTIONS.ADJUST, documentId);
        
        // Create a new ADJUST task
        await createTask({
          action: 'ADJUST',
          due_at: dueDateString,
          assigned_user_id: assignedUserId,
          document_id: documentId,
          description: `Revise document ${documentName}`
        });
        
        logger.info('Created ADJUST task for document', LogCategory.GENERAL, {
          documentId,
          assignedUserId,
          dueDate: dueDateString
        });
      } else {
        logger.warn('No completed UPLOAD tasks found for document', LogCategory.GENERAL, {
          documentId
        });
      }
    };


    /**
     * Gets the name of a forecasting process by its ID
     * @param forecastId The ID of the forecasting process
     * @returns Promise<string> The name of the forecasting process or 'Unknown Process' if not found
     */
    const getForecastProcessName = async (forecastId: string): Promise<string> => {
      try {
        const { data: processData, error: processError } = await supabase
          .from('forecasting_process')
          .select('name')
          .eq('id', forecastId)
          .single();
          
        if (processError) {
          logger.error('Error fetching process name', LogCategory.DATABASE, { error: processError, forecastId });
          return 'Unknown Process';
        }
        
        return processData?.name || 'Unknown Process';
      } catch (error) {
        logger.error('Exception in getForecastProcessName', LogCategory.DATABASE, { error, forecastId });
        return 'Unknown Process';
      }
    };

    /**
     * Creates a presentation document for a forecast process
     * @param forecastId The ID of the forecasting process
     * @param forecastName The name of the forecasting process
     * @param assignedUserId The ID of the user who will be assigned to the document
     * @returns Promise<string | null> The ID of the created document or null if creation failed
     */
    const createPresentationDocument = async (
      forecastId: string,
      forecastName: string,
      assignedUserId: string
    ): Promise<string | null> => {
      try {
        const documentName = `Presentation_${forecastName}.pptx`;
        const documentDescription = `Final presentation for forecast ${forecastName}`;
        
        // Create the document
        const { data: documentData, error: documentError } = await supabase
          .from('documents')
          .insert({
            name: documentName,
            description: documentDescription,
            status: DOCUMENT_STATUS.CREATING,
            created_by: assignedUserId,
            created_at: new Date().toISOString(),
            forecast_id: forecastId,
            report_type: 'PCO'
          })
          .select()
          .single();
          
        if (documentError) {
          logger.error('Error creating presentation document', LogCategory.DATABASE, { error: documentError });
          return null;
        }
        
        logger.info('Created presentation document', LogCategory.GENERAL, {
          documentId: documentData.id,
          documentName,
          forecastId,
          assignedUserId
        });
        
        return documentData.id;
      } catch (error) {
        logger.error('Exception in createPresentationDocument', LogCategory.DATABASE, { error });
        return null;
      }
    };

    /**
     * Gets the user ID of the first user with the HEAD_OF_PCO business role
     * @returns Promise<string | null> The user ID or null if not found
     */
    const getHeadOfPcoUserId = async (): Promise<string | null> => {
      try {
        const { data: headOfPcoData, error: userError } = await supabase
          .from('profiles')
          .select('id')
          .eq('business_role', 'HEAD_OF_PCO')
          .limit(1);
          
        if (userError) {
          logger.error('Error finding HEAD_OF_PCO user', LogCategory.DATABASE, { error: userError });
          return null;
        }
        
        if (!headOfPcoData || headOfPcoData.length === 0) {
          logger.warn('No HEAD_OF_PCO user found', LogCategory.DATABASE);
          return null;
        }
        
        return headOfPcoData[0].id;
      } catch (error) {
        logger.error('Exception in getHeadOfPcoUserId', LogCategory.DATABASE, { error });
        return null;
      }
    };

    /**
     * Counts documents with REVISION_NEEDED status in a forecasting process
     * @param forecastId The ID of the forecasting process
     * @returns Promise<number | null> The count of documents or null if an error occurred
     */
    const countRevisionNeededDocuments = async (forecastId: string): Promise<number | null> => {
      if (!forecastId) {
        logger.warn('No forecast ID provided for document count', LogCategory.GENERAL);
        return null;
      }

      try {
        // Count documents with REVISION_NEEDED status in the process
        const { count, error: countError } = await supabase
          .from('documents')
          .select('*', { count: 'exact', head: true })
          .eq('forecast_id', forecastId)
          .eq('status', DOCUMENT_STATUS.REVISION_NEEDED);
        
        if (countError) {
          logger.error('Error fetching document count', LogCategory.DATABASE, { error: countError });
          return null;
        }
        
        return count || 0;
      } catch (error) {
        logger.error('Exception in countRevisionNeededDocuments', LogCategory.DATABASE, { error });
        return null;
      }
    };

    /**
     * Creates a presentation task if the number of documents with REVISION_NEEDED status
     * in the same forecasting process reaches the threshold
     * @param forecastId The ID of the forecasting process
     * @returns Promise<void>
     */
    const createPresentationTaskOnReviewCompletion = async (forecastId: string): Promise<void> => {
      if (!forecastId) {
        logger.warn('No forecast ID provided for presentation task creation', LogCategory.GENERAL);
        return;
      }

      try {
        // Count documents with REVISION_NEEDED status in the process using the extracted function
        const count = await countRevisionNeededDocuments(forecastId);
        
        if (count === null) {
          logger.error('Failed to get document count for presentation task', LogCategory.GENERAL);
          return;
        }

        // Check if count equals the threshold
        if (count === PRESENTATION_DOCUMENT_COUNT) {
          logger.info(`Document count (${count}) reached threshold (${PRESENTATION_DOCUMENT_COUNT}), creating presentation task`, LogCategory.GENERAL);
          
          // Get the forecast process name using the extracted function
          const forecastName = await getForecastProcessName(forecastId);
          
          // Find the HEAD_OF_PCO user using the extracted function
          const assignedUserId = await getHeadOfPcoUserId();
          
          if (!assignedUserId) {
            logger.error('No HEAD_OF_PCO user found for presentation task', LogCategory.DATABASE);
            return;
          }
          
          // Create a presentation document using the extracted function
          const documentId = await createPresentationDocument(forecastId, forecastName, assignedUserId);
          
          // Calculate due date using the getDueDate function
          const { getDueDate } = useDueDates();
          const dueDateString = await getDueDate(ACTIONS.PRESENT, documentId as string);
          
          if (!documentId) {
            logger.error('Failed to create presentation document', LogCategory.GENERAL);
            return;
          }
          
          // Create a new PRESENT task with the new document ID
          await createTask({
            action: 'PRESENT',
            due_at: dueDateString,
            assigned_user_id: assignedUserId,
            document_id: documentId,
            description: `Create presentation for ${forecastName}`
          });
          
          logger.info('Created PRESENT task for process', LogCategory.GENERAL, {
            processId: forecastId,
            processName: forecastName,
            documentId,
            assignedUserId,
            dueDate: dueDateString
          });
        }
      } catch (error) {
        logger.error('Error in presentation task creation', LogCategory.GENERAL, { error });
      }
    };

    // Format date for display
    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    };

    // Format document status for display
    const formatDocumentStatus = (status: DOCUMENT_STATUS): string => {
      switch (status) {
        case DOCUMENT_STATUS.CREATING:
          return 'Creating';
        case DOCUMENT_STATUS.REVIEW_CREATED:
          return 'Review Created';
        case DOCUMENT_STATUS.EMAIL_PENDING:
          return 'Email Pending';
        case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
          return 'Ready for Download';
        case DOCUMENT_STATUS.DOWNLOADED:
          return 'Downloaded';
        case DOCUMENT_STATUS.UPLOADED_CHECKING:
          return 'Uploaded and Checking';
        case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
          return 'Adjustment Needed';
        case DOCUMENT_STATUS.REVISION_NEEDED:
          return 'Ready for Revision';
        default:
          return status;
      }
    };

    // Close the modal
    const close = () => {
      emit('close');
    };

    // Approve the template
    const approveTemplate = async () => {
      if (!props.documentData || !props.documentData.id) {
        errorMessage.value = 'No document data provided';
        return;
      }

      isLoading.value = true;
      successMessage.value = null;
      errorMessage.value = null;

      try {
        logger.info('Approving uploaded template', LogCategory.GENERAL, {
          documentId: props.documentData.id,
          previousStatus: props.documentData.status,
          newStatus: DOCUMENT_STATUS.REVISION_NEEDED
        });

        // Emit event to update the document status
        emit('status-updated', {
          documentId: props.documentData.id,
          newStatus: DOCUMENT_STATUS.REVISION_NEEDED
        });
        
        // Complete all review tasks for this document with the result 'approved'
        await completeReviewTasksForDocument(props.documentData.id, props.documentData.name, 'approved');

        console.log('Completed review tasks for document', props.documentData);       // Fetch count of documents with REVISION_NEEDED status in the same forecasting process
        if (props.documentData.forecast_id) {
          await createPresentationTaskOnReviewCompletion(props.documentData.forecast_id);
        }
        
        // Check if we need to create a presentation task based on process_id
        if (props.documentData.process_id) {
          await createPresentationTaskOnReviewCompletion(props.documentData.process_id);
        }
        
        successMessage.value = 'Template approved successfully';

        // Close the modal after a short delay
        setTimeout(() => {
          close();
        }, 1500);
      } catch (error) {
        logger.error('Error approving template', LogCategory.GENERAL, { error });
        errorMessage.value = error instanceof Error ? error.message : 'An unknown error occurred';
      } finally {
        isLoading.value = false;
      }
    };

    // Decline the template
    const declineTemplate = async () => {
      if (!props.documentData || !props.documentData.id) {
        errorMessage.value = 'No document data provided';
        return;
      }

      isLoading.value = true;
      successMessage.value = null;
      errorMessage.value = null;

      try {
        logger.info('Declining uploaded template', LogCategory.GENERAL, {
          documentId: props.documentData.id,
          previousStatus: props.documentData.status,
          newStatus: DOCUMENT_STATUS.ADJUSTMENT_NEEDED
        });

        // Emit event to update the document status
        emit('status-updated', {
          documentId: props.documentData.id,
          newStatus: DOCUMENT_STATUS.ADJUSTMENT_NEEDED
        });

        // Complete all review tasks for this document with the result 'rejected'
        await completeReviewTasksForDocument(props.documentData.id, props.documentData.name, 'rejected');

        // Create an ADJUST task for the document assigned to the original uploader
        await createAdjustTaskForDocument(props.documentData.id, props.documentData.name);

        successMessage.value = 'Template declined successfully';

        // Close the modal after a short delay
        setTimeout(() => {
          close();
        }, 1500);
      } catch (error) {
        logger.error('Error declining template', LogCategory.GENERAL, { error });
        errorMessage.value = error instanceof Error ? error.message : 'An unknown error occurred';
      } finally {
        isLoading.value = false;
      }
    };

    return {
      isLoading,
      successMessage,
      errorMessage,
      formatDate,
      formatDocumentStatus,
      close,
      approveTemplate,
      declineTemplate,
      DOCUMENT_STATUS,
      commentsVisible
    };
  }
});
</script>
